// Components & Hooks
import { Card } from "@components/cards/card";
import { useState } from "react";
import { api } from "@service/api";
import { Button } from "@components/buttons/button";
import { ModalInfoHtml } from "@components/modals/modal-info/indexHtml";
import { IModalInfoHtmlDTO } from "@hooks/dtos/IModalInfoDTO";
import { useAuthContext } from "@hooks/contexts/auth-context";
import { formatName } from "@hooks/functions/formatName";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

// Icons
import {
  FaArrowRight,
  FaBell,
  FaEye,
  FaEyeSlash,
  FaLeaf,
  FaLock,
  FaMapMarkedAlt,
  FaRegIdCard,
  FaRegUser,
  FaSearch,
  FaSearchLocation,
  FaShieldAlt,
  FaTruck,
} from "react-icons/fa";

interface ILoginFormValues {
  document: string;
  password: string;
}

export function Home() {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();
  const { customer, signIn, signOut } = useAuthContext();
  const navigate = useNavigate();
  const loggedCustomer = customer?.customer;

  const [trackingCode, setTrackingCode] = useState<string>("");
  const [trackingCodeError, setTrackingCodeError] = useState<string>("");
  const [showLoginPassword, setShowLoginPassword] = useState<boolean>(false);
  const [modal, setModal] = useState<IModalInfoHtmlDTO>({
    header: "",
    title: "",
    html: "",
  });

  const loginScheme = yup.object().shape({
    document: yup.string().required("CPF é obrigatório"),
    password: yup.string().required("Senha é obrigatória"),
  });

  const {
    register,
    handleSubmit: handleLoginSubmit,
    formState: { errors },
  } = useForm<ILoginFormValues>({
    resolver: yupResolver(loginScheme) as any,
    mode: "onSubmit",
    reValidateMode: "onBlur",
  });

  const modalContent = (type: "firstAccess"): IModalInfoHtmlDTO => {
    switch (type) {
      case "firstAccess":
        return {
          header: "Primeiro Acesso?",
          title: "Instruções de Primeiro Acesso:",
          html: (
            <>
              <p className="mt-3">
                Para o primeiro acesso, utilize o seu <strong>CPF</strong> como
                Login e Senha.
              </p>
              Na senha, utilize <strong>somente números</strong> do seu CPF.
            </>
          ),
        };

      default:
        return { header: "", title: "", html: "" };
    }
  };

  const handleTrackingSubmit = async () => {
    if (!trackingCode) {
      return setTrackingCodeError(
        `É necessário digitar um código de rastreio.`,
      );
    }

    try {
      const params = `?codeId=${trackingCode}`;
      const result = await api.get(`/misc/tracking/code${params}`);
      const tracking = result.data;

      if (!tracking) {
        return pushToastify({
          message: "Código não localizado, verifique e tente novamente.",
          type: "info",
          theme: "dark",
          pauseOnHover: true,
        });
      }

      pushToastify({
        message: "Pedido localizado com sucesso, redirecionamento iniciado...",
        type: "success",
        theme: "dark",
        pauseOnHover: true,
      });

      setTimeout(() => {
        window.location.href = `/tracking/${trackingCode}`;
      }, 750);
    } catch (error: any) {
      console.log({ error });
      pushToastify({
        message:
          error?.response?.data?.message ||
          "Houve um erro ao consultar o código informado!",
        type: "error",
        theme: "dark",
        pauseOnHover: true,
      });
    }
  };

  async function handleLogin(data: ILoginFormValues) {
    await signIn(data);
  }

  return (
    <>
      {/* HERO SECTION */}
      <section
        className="container-fluid"
        style={{
          minHeight: "calc(100vh - 76px)",
          background:
            "linear-gradient(135deg, #ffffff 0%, #fbfdff 45%, #eefbfc 100%)",
          paddingTop: "clamp(4rem, 7vw, 6rem)",
          paddingBottom: "clamp(4rem, 7vw, 6rem)",
        }}
      >
        <div
          className="container d-flex flex-column"
          style={{ minHeight: "calc(100vh - 76px - 12rem)" }}
        >
          <div className="text-center mb-5">
            <h1
              className="fw-bold mb-2"
              style={{
                color: "#10245d",
                fontSize: "clamp(2rem, 3.5vw, 2.75rem)",
                lineHeight: "1.1",
              }}
            >
              <span style={{ color: "#0ea5a8" }}>Entrar</span> ou rastrear
              pedido
            </h1>
            <p className="fs-5 text-muted mb-0">
              Acesse sua conta ou acompanhe seu pedido com poucos cliques.
            </p>
          </div>

          <div
            className="row g-4 justify-content-center mx-auto"
            style={{ maxWidth: "1040px" }}
          >
            <div className="col-12 col-md-6">
              <Card
                className="h-100 p-4 p-lg-5 rounded-4 bg-white d-flex flex-column"
                style={{
                  border: "1px solid rgba(14, 165, 168, 0.18)",
                  boxShadow: "0 1rem 2.5rem rgba(15, 23, 42, 0.05)",
                  minHeight: "360px",
                }}
              >
                <div className="d-flex align-items-start gap-4 mb-4">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: "68px",
                      height: "68px",
                      backgroundColor: "rgba(14, 165, 168, 0.12)",
                      color: "#0ea5a8",
                    }}
                  >
                    <FaRegUser size={34} />
                  </div>

                  <div>
                    <h3 className="fw-bold mb-1" style={{ color: "#10245d" }}>
                      Entrar
                    </h3>
                    <p className="text-muted mb-0">
                      Acesse sua conta para gerenciar seus pedidos.
                    </p>
                  </div>
                </div>

                {loggedCustomer ? (
                  <div className="d-flex flex-column gap-3">
                    <div className="rounded-4 bg-light p-3 mb-1">
                      <span
                        className="badge mb-2"
                        style={{
                          backgroundColor: "rgba(25, 135, 84, 0.12)",
                          color: "#198754",
                        }}
                      >
                        Sessão ativa
                      </span>
                      <p className="text-muted mb-1">Está logado como:</p>
                      <h5 className="fw-bold mb-1" style={{ color: "#10245d" }}>
                        {formatName(loggedCustomer.name)}
                      </h5>
                    </div>

                    <Button
                      className="btn btn-logo-primary text-white w-100 py-3 rounded-pill shadow-sm"
                      onClick={() => navigate("/dashboard")}
                    >
                      <span className="fw-bold">Ir para Dashboard</span>
                      <FaArrowRight className="ms-2" />
                    </Button>

                    <Button
                      className="btn btn-outline-danger w-100 py-3 rounded-pill"
                      onClick={signOut}
                    >
                      <span className="fw-bold">Sair da conta</span>
                    </Button>
                  </div>
                ) : (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleLoginSubmit(handleLogin)();
                    }}
                  >
                    <div className="mb-3">
                      <div className="input-group input-group-lg">
                        <span className="input-group-text bg-white border-end-0">
                          <FaRegIdCard className="text-muted" />
                        </span>
                        <input
                          className={`form-control border-start-0 ${
                            errors?.document?.message ? "is-invalid" : ""
                          }`}
                          placeholder="CPF"
                          style={{ fontSize: "0.95rem" }}
                          type="text"
                          {...register("document")}
                        />
                      </div>
                      {errors?.document?.message && (
                        <div className="invalid-feedback d-block">
                          {errors.document.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-4">
                      <div className="input-group input-group-lg">
                        <span className="input-group-text bg-white border-end-0">
                          <FaLock className="text-muted" />
                        </span>
                        <input
                          className={`form-control border-start-0 border-end-0 ${
                            errors?.password?.message ? "is-invalid" : ""
                          }`}
                          placeholder="Senha"
                          style={{ fontSize: "0.95rem" }}
                          type={showLoginPassword ? "text" : "password"}
                          {...register("password")}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-light border text-muted"
                          onClick={() =>
                            setShowLoginPassword((prev) => !prev)
                          }
                        >
                          {showLoginPassword ? <FaEye /> : <FaEyeSlash />}
                        </button>
                      </div>
                      {errors?.password?.message && (
                        <div className="invalid-feedback d-block">
                          {errors.password.message}
                        </div>
                      )}
                    </div>

                    <Button className="btn btn-logo-primary text-white w-100 py-3 rounded-pill shadow-sm">
                      <span className="fw-bold">Entrar</span>
                      <FaArrowRight className="ms-2" />
                    </Button>

                    <button
                      type="button"
                      className="btn btn-link text-primary w-100 mt-3"
                      onClick={() => {
                        const content = modalContent("firstAccess");
                        setModal((prev) => ({
                          ...prev,
                          header: content.header,
                          title: content.title,
                          html: content.html,
                        }));
                      }}
                    >
                      Primeiro acesso?
                    </button>
                  </form>
                )}
              </Card>
            </div>

            <div className="col-12 col-md-6">
              <Card
                className="h-100 p-4 p-lg-5 rounded-4 bg-white d-flex flex-column"
                style={{
                  border: "1px solid rgba(59, 130, 246, 0.16)",
                  boxShadow: "0 1rem 2.5rem rgba(15, 23, 42, 0.05)",
                  minHeight: "360px",
                }}
              >
                <div className="d-flex align-items-start gap-4 mb-4">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: "68px",
                      height: "68px",
                      backgroundColor: "rgba(59, 130, 246, 0.12)",
                      color: "#1d7eea",
                    }}
                  >
                    <FaTruck size={34} />
                  </div>

                  <div>
                    <h3 className="fw-bold mb-1" style={{ color: "#10245d" }}>
                      Rastrear pedido
                    </h3>
                    <p className="text-muted mb-0">
                      Acompanhe seu pedido de forma rápida e simples.
                    </p>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="input-group input-group-lg">
                    <span className="input-group-text bg-white border-end-0">
                      <FaSearch className="text-muted" />
                    </span>
                    <input
                      className={`form-control border-start-0 ${
                        trackingCodeError ? "is-invalid" : ""
                      }`}
                      placeholder="Nº do pedido ou código de rastreio"
                      style={{ fontSize: "0.95rem" }}
                      type="text"
                      value={trackingCode}
                      onChange={(e) => {
                        const code = e.target.value;
                        setTrackingCode(code);
                        if (code.length > 0) {
                          setTrackingCodeError("");
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && trackingCode.length > 2) {
                          e.preventDefault();
                          handleTrackingSubmit();
                        }
                      }}
                    />
                  </div>
                  {trackingCodeError && (
                    <div className="invalid-feedback d-block">
                      {trackingCodeError}
                    </div>
                  )}
                </div>

                <div
                  className="mb-4"
                  style={{ height: loggedCustomer ? "54px" : "42px" }}
                ></div>

                <Button
                  className="btn btn-info text-white w-100 py-3 rounded-pill shadow-sm"
                  onClick={handleTrackingSubmit}
                >
                  <span className="fw-bold">Buscar pedido</span>
                  <FaArrowRight className="ms-2" />
                </Button>
              </Card>
            </div>
          </div>

          <div className="row justify-content-center mt-auto pt-5">
            <div className="col-12 col-lg-9">
              <div className="d-flex flex-column flex-md-row align-items-center justify-content-center gap-4 text-muted">
                <div className="d-none d-md-block flex-grow-1 border-top"></div>
                <div className="d-flex align-items-center gap-2">
                  <span
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: "44px",
                      height: "44px",
                      backgroundColor: "rgba(14, 165, 168, 0.12)",
                      color: "#0ea5a8",
                    }}
                  >
                    <FaShieldAlt />
                  </span>
                  <span>Simples</span>
                </div>
                <div className="vr d-none d-md-block"></div>
                <div className="d-flex align-items-center gap-2">
                  <span
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: "44px",
                      height: "44px",
                      backgroundColor: "rgba(14, 165, 168, 0.12)",
                      color: "#0ea5a8",
                    }}
                  >
                    <FaLock />
                  </span>
                  <span>Seguro</span>
                </div>
                <div className="vr d-none d-md-block"></div>
                <div className="d-flex align-items-center gap-2">
                  <span
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: "44px",
                      height: "44px",
                      backgroundColor: "rgba(14, 165, 168, 0.12)",
                      color: "#0ea5a8",
                    }}
                  >
                    <FaLeaf />
                  </span>
                  <span>Transparente</span>
                </div>
                <div className="d-none d-md-block flex-grow-1 border-top"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES SECTION */}
      <section
        className="container-fluid"
        style={{
          backgroundColor: "#ffffff",
          paddingTop: "clamp(3rem, 6vw, 6rem)",
          paddingBottom: "clamp(3rem, 6vw, 6rem)",
          paddingLeft: "clamp(1rem, 8vw, 14rem)",
          paddingRight: "clamp(1rem, 8vw, 14rem)",
        }}
      >
        <div className="row mb-5 text-center">
          <div className="col">
            <h2
              className="fw-bold mb-3"
              style={{
                color: "#0f172a",
                fontSize: "clamp(1.6rem, 3vw, 2.3rem)",
              }}
            >
              Por que usar nosso rastreio?
            </h2>
            <p
              style={{
                color: "#64748b",
                maxWidth: "620px",
                margin: "0 auto",
                lineHeight: "1.6",
              }}
            >
              Desenvolvido para oferecer confiança, clareza e comodidade em cada
              etapa da sua entrega.
            </p>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-12 col-md-6 col-lg-4">
            <Card className="h-100 p-4 border-0 rounded-4 shadow-sm">
              <div className="d-flex flex-column align-items-start gap-3">
                <FaSearchLocation className="fs-2 text-info" />
                <h5 className="fw-semibold" style={{ color: "#0f172a" }}>
                  Rastreio Transparente
                </h5>
                <p className="mb-0" style={{ color: "#64748b" }}>
                  Acompanha cada etapa da sua entrega com transparência e
                  segurança
                </p>
              </div>
            </Card>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <Card className="h-100 p-4 border-0 rounded-4 shadow-sm">
              <div className="d-flex flex-column align-items-start gap-3">
                <FaMapMarkedAlt className="fs-2 text-info" />
                <h5 className="fw-semibold" style={{ color: "#0f172a" }}>
                  Status de Entrega do Pedido
                </h5>
                <p className="mb-0" style={{ color: "#64748b" }}>
                  Sabe exatamente onde, e qual etapa, seu pedido está.
                </p>
              </div>
            </Card>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <Card className="h-100 p-4 border-0 rounded-4 shadow-sm">
              <div className="d-flex flex-column align-items-start gap-3">
                <FaBell className="fs-2 text-info" />
                <h5 className="fw-semibold" style={{ color: "#0f172a" }}>
                  Controle e Planejamento
                </h5>
                <p className="mb-0" style={{ color: "#64748b" }}>
                  Mais controle: monitore sua encomenda de forma simples e
                  rápida.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section
        className="container-fluid d-flex justify-content-center align-items-center text-center"
        style={{
          background:
            "linear-gradient(135deg, rgba(127,211,216,0.25), rgba(185,234,237,0.35))",
          padding: "clamp(3rem, 6vw, 6rem)",
        }}
      >
        <div className="col-12 col-lg-8">
          <h2
            className="fw-bold mb-3"
            style={{ color: "#0f172a", fontSize: "clamp(1.5rem, 3vw, 2.2rem)" }}
          >
            Pronto para acompanhar seu pedido?
          </h2>
          <p
            className="mb-4"
            style={{
              fontSize: "1.05rem",
              color: "#475569",
              maxWidth: "620px",
              margin: "0 auto",
            }}
          >
            Insira seu código de rastreio e tenha total controle da sua entrega.
          </p>

          <Button
            className="btn btn-info text-white btn-lg px-5 py-3 rounded-pill shadow-sm"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <span className="fw-bold">Buscar agora</span>
          </Button>
        </div>
      </section>

      <ModalInfoHtml
        isOpen={modal?.header !== ""}
        onClose={() => {
          setModal((prev) => ({
            ...prev,
            header: "",
            title: "",
            html: "",
          }));
        }}
        header={modal?.header || ""}
        title={modal?.title || ""}
        html={modal?.html || ""}
      />
    </>
  );
}
